import { useState } from 'react';
import { ApiError } from '@/api/client';
import { createShop, deleteShop, updateShop } from './api';
import type { Shop } from './types';
import s from './AssemblyLineSheet.module.css';

const READONLY = import.meta.env.VITE_READONLY !== '0';

export function ShopSheet({ shop, isDefault = false, onClose, onSaved }: { shop?: Shop; isDefault?: boolean; onClose: () => void; onSaved: () => void }) {
  const [name, setName] = useState(shop?.name || '');
  const [isActive, setIsActive] = useState(shop?.isActive ?? true);
  const [saving, setSaving] = useState(false);
  const [uncertain, setUncertain] = useState(false);
  const [error, setError] = useState('');
  const used = !!shop?.orderCount;
  const save = async () => {
    if (READONLY || saving || uncertain) return;
    if (!name.trim()) return setError('请输入店铺名称');
    setSaving(true); setError('');
    try {
      if (shop) await updateShop(shop.id, { name: name.trim(), isActive, expectedUpdatedAt: shop.updatedAt });
      else await createShop(name.trim());
      onSaved();
    } catch (cause) {
      const unknown = !(cause instanceof ApiError) || cause.status === 0 || cause.status >= 500;
      setUncertain(unknown);
      setError(unknown ? '保存结果未确认，请关闭窗口核对列表后再操作。' : cause instanceof Error ? cause.message : '保存失败');
    } finally { setSaving(false); }
  };
  return <div className={s.backdrop} role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget && !saving) onClose(); }}><section className={s.sheet} role="dialog" aria-modal="true" aria-label={shop ? `编辑店铺 ${shop.name}` : '新增店铺'}>
    <header><h2>{shop ? `编辑店铺 · ${shop.name}` : '新增店铺'}</h2><button type="button" aria-label="关闭" disabled={saving} onClick={onClose}>×</button></header>
    <div className={s.body}><div className={s.fields}><label>店铺名称 <em>*</em><input autoFocus maxLength={100} value={name} disabled={used} onChange={(event) => setName(event.target.value)} /></label>{shop && <label>状态<select value={isActive ? 'active' : 'inactive'} onChange={(event) => setIsActive(event.target.value === 'active')}><option value="active">启用</option><option value="inactive" disabled={isDefault}>停用</option></select></label>}</div>
      {used ? <div className={s.notice}><b>已有 {shop?.orderCount} 张订单使用此店铺</b><p>V1 订单按店铺名称关联。旧订单仍可编辑；停用后不再用于新订单。</p></div> : <p className={s.hint}>店铺名称在新建订单时可选；有订单使用后名称将保持固定。</p>}
      {isDefault && <p className={s.hint}>默认店铺不能停用，请先将其他店铺设为默认。</p>}
      {error && <p className={s.error} role="alert">{error}</p>}{READONLY && <p className={s.hint}>当前为只读预览，不能保存。</p>}
    </div><footer><button type="button" onClick={onClose} disabled={saving}>取消</button><button type="button" className={s.primary} disabled={READONLY || saving || uncertain || used && name.trim() !== shop?.name || name.trim() === shop?.name && isActive === shop?.isActive} onClick={() => void save()}>{saving ? '保存中…' : '保存'}</button></footer>
  </section></div>;
}

export function ShopDeleteDialog({ shop, isDefault, onClose, onDeleted }: { shop: Shop; isDefault: boolean; onClose: () => void; onDeleted: () => void }) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const blocked = isDefault || !!shop.orderCount;
  const remove = async () => {
    if (READONLY || blocked || saving) return;
    setSaving(true); setError('');
    try { await deleteShop(shop.id); onDeleted(); }
    catch (cause) { setError(cause instanceof Error ? cause.message : '删除失败'); }
    finally { setSaving(false); }
  };
  return <div className={s.backdrop} role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget && !saving) onClose(); }}><section className={s.sheet} role="dialog" aria-modal="true" aria-label={`删除店铺 ${shop.name}`}>
    <header><h2>删除店铺「{shop.name}」？</h2><button type="button" aria-label="关闭" disabled={saving} onClick={onClose}>×</button></header>
    <div className={s.body}>{blocked ? <div className={s.notice}><b>当前不能删除</b><p>{isDefault ? '请先把其他店铺设为默认。' : ''}{shop.orderCount ? `已有 ${shop.orderCount} 张订单使用此店铺，删除会让 V1 无法继续编辑这些订单。` : ''}</p></div> : <p className={s.hint}>这个店铺没有订单，删除后将从 V1 与 V2 的下单选项中移除。</p>}{error && <p role="alert" className={s.error}>{error}</p>}</div>
    <footer><button type="button" onClick={onClose} disabled={saving}>取消</button><button type="button" className={s.danger} disabled={READONLY || blocked || saving} onClick={() => void remove()}>{saving ? '删除中…' : '确认删除'}</button></footer>
  </section></div>;
}
